import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Shield, ScrollText, CheckCircle, XCircle, Clock, UserPlus, UserCheck,
  UserX, Search, RefreshCw, Plus, Trash2, Lock, Eye, Settings, Grid3X3,
  ArrowRightLeft, AlertTriangle, Users, Pencil
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "../components/ui/select";
import Layout from "../components/Layout";
import { API } from "../App";
import { toast } from "sonner";
import { userNameIsOrphan, userDisplayName } from "../utils/userDisplay";

const STATUS_STYLES = {
  active: "bg-emerald-100 text-emerald-700", pending: "bg-amber-100 text-amber-700",
  rejected: "bg-red-100 text-red-700", inactive: "bg-slate-100 text-slate-500",
  approved: "bg-emerald-100 text-emerald-700",
};
const ROLE_STYLES = {
  admin: "bg-slate-800 text-white", ceo: "bg-amber-800 text-white", hod_sales: "bg-indigo-100 text-indigo-700",
  sales_head: "bg-blue-100 text-blue-700",
  sales_rep: "bg-slate-100 text-slate-600", telecaller: "bg-orange-100 text-orange-700",
  marketing_manager: "bg-purple-100 text-purple-700", sales_manager: "bg-teal-100 text-teal-700",
};

const ROLE_LABELS = {
  admin: "Admin", ceo: "CEO", hod_sales: "HOD Sales", sales_head: "Sales Head", sales_rep: "Sales Rep",
  telecaller: "Telecaller", marketing_manager: "Marketing Manager", sales_manager: "Sales Manager", pending: "Pending",
};

const GovernancePage = () => {
  const [tab, setTab] = useState("users");

  // Duplicates (display-name collisions)
  const [duplicates, setDuplicates] = useState([]);
  const [duplicatesLoading, setDuplicatesLoading] = useState(false);
  const [dupReassignFrom, setDupReassignFrom] = useState(null);
  const [dupReassignTo, setDupReassignTo] = useState(null);
  const [dupReassignDeactivate, setDupReassignDeactivate] = useState(true);
  const [dupReassignBusy, setDupReassignBusy] = useState(false);

  // Users
  const [users, setUsers] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [approveOpen, setApproveOpen] = useState(false);
  const [approveUser, setApproveUser] = useState(null);
  const [approveRole, setApproveRole] = useState("sales_rep");
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectUser, setRejectUser] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [addUserOpen, setAddUserOpen] = useState(false);
  const [addUserForm, setAddUserForm] = useState({ email: "", name: "", role: "sales_rep" });
  const [matrixData, setMatrixData] = useState(null);
  const [matrixLoading, setMatrixLoading] = useState(false);

  // Deactivate with Reassignment
  const [deactivateOpen, setDeactivateOpen] = useState(false);
  const [deactivateUser, setDeactivateUser] = useState(null);
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameUser, setRenameUser] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [deactivateWorkload, setDeactivateWorkload] = useState(null);
  const [reassignTo, setReassignTo] = useState("");
  const [deactivating, setDeactivating] = useState(false);

  // Bulk Reassign
  const [bulkReassignOpen, setBulkReassignOpen] = useState(false);
  const [bulkFromUser, setBulkFromUser] = useState("");
  const [bulkToUser, setBulkToUser] = useState("");
  const [bulkFromWorkload, setBulkFromWorkload] = useState(null);
  const [bulkReassigning, setBulkReassigning] = useState(false);

  // Audit logs
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logFilter, setLogFilter] = useState({ action: "all", entity_type: "all" });
  const [logMeta, setLogMeta] = useState({ actions: [], entity_types: [] });

  // Approvals
  const [approvals, setApprovals] = useState([]);
  const [approvalsLoading, setApprovalsLoading] = useState(false);
  const [approvalFilter, setApprovalFilter] = useState("all");
  const [createApprovalOpen, setCreateApprovalOpen] = useState(false);
  const [approvalForm, setApprovalForm] = useState({ type: "", entity_type: "", entity_id: "", description: "" });
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewApproval, setReviewApproval] = useState(null);
  const [reviewDecision, setReviewDecision] = useState("");
  const [reviewNotes, setReviewNotes] = useState("");

  // Approval rules
  const [approvalRules, setApprovalRules] = useState([]);
  const [addRuleOpen, setAddRuleOpen] = useState(false);
  const [ruleForm, setRuleForm] = useState({ name: "", description: "", trigger_type: "", approver_role: "admin" });

  // Load data based on tab
  useEffect(() => {
    if (tab === "users") loadUsers();
    if (tab === "audit") loadLogs();
    if (tab === "approvals") { loadApprovals(); loadApprovalRules(); }
  }, [tab]);

  const loadUsers = async () => {
    setUsersLoading(true);
    try {
      const [allRes, pendRes] = await Promise.all([
        axios.get(`${API}/governance/users/all`),
        axios.get(`${API}/governance/users/pending`),
      ]);
      setUsers(allRes.data);
      setPendingUsers(pendRes.data);
    } catch { toast.error("Failed to load users"); }
    finally { setUsersLoading(false); }
  };

  const loadLogs = async () => {
    setLogsLoading(true);
    try {
      const params = {};
      if (logFilter.action !== "all") params.action = logFilter.action;
      if (logFilter.entity_type !== "all") params.entity_type = logFilter.entity_type;
      const [logsRes, metaRes] = await Promise.all([
        axios.get(`${API}/governance/audit-logs`, { params }),
        axios.get(`${API}/governance/audit-logs/actions`),
      ]);
      setLogs(logsRes.data);
      setLogMeta(metaRes.data);
    } catch { toast.error("Failed to load audit logs"); }
    finally { setLogsLoading(false); }
  };

  useEffect(() => { if (tab === "audit") loadLogs(); }, [logFilter]);

  const loadApprovals = async () => {
    setApprovalsLoading(true);
    try {
      const params = approvalFilter !== "all" ? { status: approvalFilter } : {};
      const res = await axios.get(`${API}/governance/approvals`, { params });
      setApprovals(res.data);
    } catch { toast.error("Failed to load approvals"); }
    finally { setApprovalsLoading(false); }
  };

  useEffect(() => { if (tab === "approvals") loadApprovals(); }, [approvalFilter]);

  const loadApprovalRules = async () => {
    try {
      const res = await axios.get(`${API}/governance/approval-rules`);
      setApprovalRules(res.data);
    } catch {}
  };

  const handleApprove = async () => {
    try {
      await axios.post(`${API}/governance/users/${approveUser.user_id}/approve`, { role: approveRole });
      toast.success(`${approveUser.name} approved as ${approveRole}`);
      setApproveOpen(false);
      loadUsers();
    } catch { toast.error("Failed to approve"); }
  };

  const handleReject = async () => {
    try {
      await axios.post(`${API}/governance/users/${rejectUser.user_id}/reject`, { reason: rejectReason });
      toast.success("User rejected");
      setRejectOpen(false);
      loadUsers();
    } catch { toast.error("Failed to reject"); }
  };

  const openDeactivateDialog = async (user) => {
    setDeactivateUser(user);
    setReassignTo("");
    setDeactivateWorkload(null);
    setDeactivateOpen(true);
    // Load workload
    try {
      const res = await axios.get(`${API}/governance/users/${user.user_id}/workload`);
      setDeactivateWorkload(res.data.workload);
    } catch (e) {
      console.error("Failed to load workload", e);
    }
  };

  const handleDeactivate = async () => {
    setDeactivating(true);
    try {
      const payload = reassignTo ? { reassign_to: reassignTo } : {};
      const res = await axios.post(`${API}/governance/users/${deactivateUser.user_id}/deactivate`, payload);
      if (res.data.reassignment) {
        const stats = res.data.reassignment;
        toast.success(`User deactivated. Reassigned: ${stats.leads} leads, ${stats.tasks} tasks, ${stats.site_visits} visits`);
      } else {
        toast.success("User deactivated");
      }
      setDeactivateOpen(false);
      loadUsers();
    } catch (e) { 
      toast.error(e.response?.data?.detail || "Failed to deactivate"); 
    }
    setDeactivating(false);
  };

  const handleReactivate = async (userId) => {
    try {
      await axios.post(`${API}/governance/users/${userId}/reactivate`);
      toast.success("User reactivated");
      loadUsers();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to reactivate"); }
  };

  const openRename = (u) => {
    setRenameUser(u);
    setRenameValue((u.name || "").trim());
    setRenameOpen(true);
  };

  const handleRenameSubmit = async () => {
    const newName = renameValue.trim();
    if (!newName) { toast.error("Name cannot be empty"); return; }
    try {
      await axios.patch(`${API}/governance/users/${renameUser.user_id}/name`,
        { name: newName });
      toast.success(`Renamed to ${newName}`);
      setRenameOpen(false);
      setRenameUser(null);
      loadUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to rename");
    }
  };

  const openBulkReassignDialog = () => {
    setBulkFromUser("");
    setBulkToUser("");
    setBulkFromWorkload(null);
    setBulkReassignOpen(true);
  };

  const loadBulkFromWorkload = async (userId) => {
    setBulkFromUser(userId);
    setBulkFromWorkload(null);
    if (!userId) return;
    try {
      const res = await axios.get(`${API}/governance/users/${userId}/workload`);
      setBulkFromWorkload(res.data.workload);
    } catch (e) {
      console.error("Failed to load workload", e);
    }
  };

  const handleBulkReassign = async () => {
    if (!bulkFromUser || !bulkToUser) {
      toast.error("Select both users");
      return;
    }
    setBulkReassigning(true);
    try {
      const res = await axios.post(`${API}/governance/bulk-reassign`, {
        from_user_id: bulkFromUser,
        to_user_id: bulkToUser
      });
      const stats = res.data.stats;
      toast.success(`Reassigned from ${res.data.from_user} to ${res.data.to_user}: ${stats.leads} leads, ${stats.tasks} tasks, ${stats.site_visits} visits`);
      setBulkReassignOpen(false);
      loadUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to reassign");
    }
    setBulkReassigning(false);
  };

  const handleAddUser = async () => {
    if (!addUserForm.email || !addUserForm.name) {
      toast.error("Email and name are required");
      return;
    }
    try {
      await axios.post(`${API}/governance/users/add`, addUserForm);
      toast.success(`${addUserForm.name} added successfully`);
      setAddUserOpen(false);
      setAddUserForm({ email: "", name: "", role: "sales_rep" });
      loadUsers();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to add user"); }
  };

  const loadMatrix = async () => {
    setMatrixLoading(true);
    try {
      const res = await axios.get(`${API}/role-matrix/permissions`, { params: { _t: Date.now() } });
      setMatrixData(res.data);
    } catch { toast.error("Failed to load permissions"); }
    setMatrixLoading(false);
  };

  const loadDuplicates = async () => {
    setDuplicatesLoading(true);
    try {
      const res = await axios.get(`${API}/governance/users/duplicate-display-names`);
      setDuplicates(res.data?.collisions || []);
    } catch { toast.error("Failed to load duplicates"); }
    setDuplicatesLoading(false);
  };

  const openReassign = (fromUser, toUser) => {
    setDupReassignFrom(fromUser);
    setDupReassignTo(toUser);
    setDupReassignDeactivate(true);
  };

  const performReassign = async () => {
    if (!dupReassignFrom || !dupReassignTo) return;
    setDupReassignBusy(true);
    try {
      const res = await axios.post(`${API}/governance/users/reassign`, {
        from_user_id: dupReassignFrom.user_id,
        to_user_id: dupReassignTo.user_id,
        deactivate_from: dupReassignDeactivate,
      });
      toast.success(
        `Moved ${res.data.leads_updated} leads + ${res.data.tasks_updated} tasks · ${res.data.teams_updated} team membership(s) updated`
      );
      setDupReassignFrom(null);
      setDupReassignTo(null);
      loadDuplicates();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reassign failed");
    } finally {
      setDupReassignBusy(false);
    }
  };

  const togglePermission = async (role, moduleKey, currentValue) => {
    if (role === "admin" || role === "ceo") return;
    try {
      await axios.put(`${API}/role-matrix/permissions/${role}`, { module: moduleKey, enabled: !currentValue });
      setMatrixData(prev => ({
        ...prev,
        permissions: {
          ...prev.permissions,
          [role]: { ...prev.permissions[role], [moduleKey]: !currentValue }
        }
      }));
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to update"); }
  };

  const submitApproval = async () => {
    try {
      await axios.post(`${API}/governance/approvals`, approvalForm);
      toast.success("Approval request submitted");
      setCreateApprovalOpen(false);
      loadApprovals();
    } catch { toast.error("Failed"); }
  };

  const submitReview = async () => {
    try {
      await axios.post(`${API}/governance/approvals/${reviewApproval.approval_id}/review`, {
        decision: reviewDecision, notes: reviewNotes,
      });
      toast.success(`Approval ${reviewDecision}`);
      setReviewOpen(false);
      loadApprovals();
    } catch { toast.error("Failed"); }
  };

  const createApprovalRule = async () => {
    try {
      await axios.post(`${API}/governance/approval-rules`, ruleForm);
      toast.success("Rule created");
      setAddRuleOpen(false);
      loadApprovalRules();
    } catch { toast.error("Failed"); }
  };

  const deleteApprovalRule = async (ruleId) => {
    try {
      await axios.delete(`${API}/governance/approval-rules/${ruleId}`);
      toast.success("Rule deleted");
      loadApprovalRules();
    } catch { toast.error("Failed"); }
  };

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto space-y-6" data-testid="governance-page">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-['Manrope']">Governance</h1>
          <p className="text-sm text-slate-500 mt-1">User onboarding, audit logs, approvals & permissions</p>
        </div>

        <Tabs value={tab} onValueChange={(v) => { setTab(v); if (v === "matrix") loadMatrix(); if (v === "duplicates") loadDuplicates(); }}>
          <TabsList className="w-full h-auto flex overflow-x-auto gap-1 p-1" data-testid="governance-tabs">
            <TabsTrigger value="users" className="text-xs whitespace-nowrap px-3 py-2 flex-shrink-0">
              Users {pendingUsers.length > 0 && <Badge className="ml-1 bg-red-500 text-white text-xs h-5 w-5 p-0 flex items-center justify-center rounded-full">{pendingUsers.length}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="matrix" data-testid="role-matrix-tab" className="text-xs whitespace-nowrap px-3 py-2 flex-shrink-0">Role Matrix</TabsTrigger>
            <TabsTrigger value="duplicates" data-testid="duplicates-tab" className="text-xs whitespace-nowrap px-3 py-2 flex-shrink-0">
              Duplicates {duplicates.length > 0 && <Badge className="ml-1 bg-amber-500 text-white text-xs h-5 w-5 p-0 flex items-center justify-center rounded-full">{duplicates.length}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="audit" className="text-xs whitespace-nowrap px-3 py-2 flex-shrink-0">Audit Logs</TabsTrigger>
            <TabsTrigger value="approvals" className="text-xs whitespace-nowrap px-3 py-2 flex-shrink-0">Approvals</TabsTrigger>
          </TabsList>

          {/* ===== USERS & ONBOARDING ===== */}
          <TabsContent value="users" className="space-y-4 mt-4">
            {/* Action Buttons */}
            <div className="flex justify-between items-center">
              <Button size="sm" variant="outline" onClick={openBulkReassignDialog} className="border-slate-300" data-testid="bulk-reassign-btn">
                <ArrowRightLeft className="w-4 h-4 mr-1.5" /> Bulk Reassign
              </Button>
              <Button size="sm" onClick={() => setAddUserOpen(true)} className="bg-slate-900 hover:bg-slate-800" data-testid="add-user-btn">
                <UserPlus className="w-4 h-4 mr-1.5" /> Add User
              </Button>
            </div>

            {/* Pending approvals */}
            {pendingUsers.length > 0 && (
              <Card className="border-amber-200 bg-amber-50/50">
                <CardHeader className="pb-2"><CardTitle className="text-base text-amber-800">Pending Approval ({pendingUsers.length})</CardTitle></CardHeader>
                <CardContent className="space-y-2">
                  {pendingUsers.map(u => (
                    <div key={u.user_id} className="flex items-center justify-between bg-white rounded-lg p-3 border" data-testid={`pending-user-${u.user_id}`}>
                      <div className="flex items-center gap-3">
                        {u.picture ? <img src={u.picture} alt="" className="w-8 h-8 rounded-full" /> : <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center"><UserPlus className="w-4 h-4 text-slate-500" /></div>}
                        <div>
                          <p className="font-medium text-slate-900">{u.name}</p>
                          <p className="text-xs text-slate-500">{u.email}</p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => { setApproveUser(u); setApproveRole("sales_rep"); setApproveOpen(true); }} data-testid={`approve-btn-${u.user_id}`}>
                          <UserCheck className="w-4 h-4 mr-1" /> Approve
                        </Button>
                        <Button size="sm" variant="destructive" onClick={() => { setRejectUser(u); setRejectReason(""); setRejectOpen(true); }} data-testid={`reject-btn-${u.user_id}`}>
                          <UserX className="w-4 h-4 mr-1" /> Reject
                        </Button>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {/* All users */}
            {usersLoading ? <div className="text-center py-8 text-slate-400"><RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" /></div> : (
              <Card>
                <CardHeader><CardTitle className="text-base">All Users ({users.length})</CardTitle></CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead><tr className="border-b text-slate-500">
                        <th className="text-left py-2 font-medium">User</th>
                        <th className="text-left py-2 font-medium">Email</th>
                        <th className="text-center py-2 font-medium">Role</th>
                        <th className="text-center py-2 font-medium">Status</th>
                        <th className="text-left py-2 font-medium">Joined</th>
                        <th className="text-right py-2 font-medium">Actions</th>
                      </tr></thead>
                      <tbody>
                        {users.map(u => (
                          <tr key={u.user_id} className="border-b last:border-0 hover:bg-slate-50" data-testid={`user-row-${u.user_id}`}>
                            <td className="py-2">
                              <div className="flex items-center gap-2">
                                {u.picture ? <img src={u.picture} alt="" className="w-6 h-6 rounded-full" /> : <div className="w-6 h-6 rounded-full bg-slate-200" />}
                                <span className="font-medium text-slate-800">{userDisplayName(u)}</span>
                                {userNameIsOrphan(u) && (
                                  <span
                                    title="Name looks auto-derived from email — click Rename to set a proper name"
                                    data-testid={`orphan-name-warn-${u.user_id}`}
                                  >
                                    <AlertTriangle className="w-3.5 h-3.5 text-amber-600" />
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="text-slate-600">{u.email}</td>
                            <td className="text-center"><Badge className={`text-xs ${ROLE_STYLES[u.role] || ""}`}>{ROLE_LABELS[u.role] || u.role}</Badge></td>
                            <td className="text-center"><Badge className={`text-xs ${STATUS_STYLES[u.status || "active"]}`}>{u.status || "active"}</Badge></td>
                            <td className="text-slate-500 text-xs">{u.created_at ? new Date(u.created_at).toLocaleDateString("en-IN") : "—"}</td>
                            <td className="text-right space-x-1">
                              {(u.status === "active" || u.status === "approved" || !u.status) && (
                                <>
                                  <Button variant="ghost" size="sm" className="text-xs text-slate-600" onClick={() => openRename(u)} data-testid={`rename-btn-${u.user_id}`}>
                                    <Pencil className="w-3 h-3 mr-1" /> Rename
                                  </Button>
                                  <Button variant="ghost" size="sm" className="text-xs text-slate-600" onClick={() => { setApproveUser(u); setApproveRole(u.role); setApproveOpen(true); }} data-testid={`change-role-btn-${u.user_id}`}>Change Role</Button>
                                  <Button variant="ghost" size="sm" className="text-xs text-red-500" onClick={() => openDeactivateDialog(u)} data-testid={`deactivate-btn-${u.user_id}`}>Deactivate</Button>
                                </>
                              )}
                              {u.status === "inactive" && (
                                <Button size="sm" variant="outline" className="text-xs text-emerald-600" onClick={() => handleReactivate(u.user_id)} data-testid={`reactivate-btn-${u.user_id}`}>Reactivate</Button>
                              )}
                              {u.status === "pending" && (
                                <Button size="sm" variant="outline" className="text-xs" onClick={() => { setApproveUser(u); setApproveRole("sales_rep"); setApproveOpen(true); }} data-testid={`approve-btn-${u.user_id}`}>Approve</Button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* ===== ROLE ACCESS MATRIX ===== */}
          <TabsContent value="matrix" className="space-y-4 mt-4">
            {matrixLoading ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="w-5 h-5 animate-spin text-slate-400" />
              </div>
            ) : matrixData ? (
              <div data-testid="role-matrix-container">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900">Role Access Matrix</h3>
                    <p className="text-xs text-slate-500">Toggle access to system modules per role. Admin always has full access.</p>
                  </div>
                  {matrixData.updated_at && (
                    <span className="text-xs text-slate-400">Last updated: {new Date(matrixData.updated_at).toLocaleString()}</span>
                  )}
                </div>
                <div className="border border-slate-200 rounded-xl overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-slate-50 border-b border-slate-200">
                        <th className="text-left py-3 px-4 font-semibold text-slate-700 sticky left-0 bg-slate-50 min-w-[180px]">Module</th>
                        {matrixData.roles.map(role => (
                          <th key={role} className="text-center py-3 px-3 font-semibold text-slate-700 min-w-[120px]">
                            {ROLE_LABELS[role] || role}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        const groups = {};
                        matrixData.modules.forEach(m => {
                          if (!groups[m.group]) groups[m.group] = [];
                          groups[m.group].push(m);
                        });
                        return Object.entries(groups).map(([group, modules]) => (
                          <React.Fragment key={group}>
                            <tr className="bg-slate-100/50">
                              <td colSpan={matrixData.roles.length + 1} className="py-2 px-4 text-xs font-bold text-slate-500 uppercase tracking-wider">{group}</td>
                            </tr>
                            {modules.map(mod => (
                              <tr key={mod.key} className="border-b border-slate-100 hover:bg-slate-50/50" data-testid={`matrix-row-${mod.key}`}>
                                <td className="py-2.5 px-4 text-slate-700 sticky left-0 bg-white">{mod.label}</td>
                                {matrixData.roles.map(role => {
                                  const enabled = matrixData.permissions[role]?.[mod.key] ?? false;
                                  const isAdmin = role === "admin" || role === "ceo";
                                  return (
                                    <td key={role} className="text-center py-2.5 px-3">
                                      <button
                                        onClick={() => !isAdmin && togglePermission(role, mod.key, enabled)}
                                        className={`w-10 h-5 rounded-full relative transition-colors duration-200 ${
                                          isAdmin ? "bg-emerald-400 cursor-not-allowed opacity-60" :
                                          enabled ? "bg-emerald-500 cursor-pointer" : "bg-slate-300 cursor-pointer"
                                        }`}
                                        disabled={isAdmin}
                                        data-testid={`toggle-${role}-${mod.key}`}
                                      >
                                        <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 ${
                                          enabled ? "translate-x-5" : "translate-x-0.5"
                                        }`} />
                                      </button>
                                    </td>
                                  );
                                })}
                              </tr>
                            ))}
                          </React.Fragment>
                        ));
                      })()}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div className="text-center py-12 text-slate-500">
                <Grid3X3 className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p>Click the "Role Matrix" tab to load the permissions matrix.</p>
              </div>
            )}
          </TabsContent>

          {/* ===== DUPLICATE DISPLAY NAMES ===== */}
          <TabsContent value="duplicates" className="space-y-4 mt-4" data-testid="duplicates-content">
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
              <div className="text-sm text-amber-900">
                <strong>Why this happens:</strong> when two user accounts render the same label in the UI
                (e.g., one has <code>name=&quot;Ashish Gupta&quot;</code> and another has blank name but
                email <code>ashish.gupta@…</code>), leads assigned to the wrong user_id silently disappear
                from the &quot;Ashish Gupta&quot; filter. Reassign all leads/tasks to the canonical user and
                deactivate the duplicate.
              </div>
            </div>

            {duplicatesLoading ? (
              <div className="text-center py-10 text-slate-500">Scanning users…</div>
            ) : duplicates.length === 0 ? (
              <div className="text-center py-10 text-slate-400">
                No duplicate display names detected. Every assignee shows as a unique label on the UI.
              </div>
            ) : (
              <div className="space-y-4">
                {duplicates.map((group) => (
                  <Card key={group.display_key} className="border-amber-200" data-testid={`dup-group-${group.display_key}`}>
                    <CardContent className="p-4 space-y-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-semibold text-slate-900">
                            {group.display_label}{" "}
                            <Badge className="ml-1 bg-amber-100 text-amber-800 border border-amber-200">
                              {group.user_count} user accounts
                            </Badge>
                          </p>
                          <p className="text-xs text-slate-500 mt-0.5">
                            {group.total_leads} leads assigned across these accounts
                          </p>
                        </div>
                      </div>

                      <table className="w-full text-xs">
                        <thead>
                          <tr className="text-slate-500 border-b border-slate-200">
                            <th className="text-left py-1.5">user_id</th>
                            <th className="text-left py-1.5">Stored name</th>
                            <th className="text-left py-1.5">Email</th>
                            <th className="text-left py-1.5">Role</th>
                            <th className="text-left py-1.5">Status</th>
                            <th className="text-right py-1.5">Leads</th>
                            <th className="text-right py-1.5">Tasks</th>
                            <th className="text-left py-1.5">Teams</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {group.users.map((u, idx) => {
                            const isCanonical = idx === 0;
                            return (
                              <tr key={u.user_id} className="border-b border-slate-100 align-top">
                                <td className="py-2 font-mono text-[10px]">{u.user_id}</td>
                                <td className="py-2">
                                  {u.name || <span className="text-slate-400 italic">(blank)</span>}
                                  {isCanonical && (
                                    <Badge className="ml-1 bg-emerald-100 text-emerald-700 border border-emerald-200 text-[10px]">
                                      canonical
                                    </Badge>
                                  )}
                                </td>
                                <td className="py-2 text-slate-600">{u.email || "—"}</td>
                                <td className="py-2">{u.role}</td>
                                <td className="py-2">
                                  <Badge
                                    className={
                                      u.status === "active"
                                        ? "bg-emerald-100 text-emerald-700 text-[10px]"
                                        : "bg-slate-100 text-slate-600 text-[10px]"
                                    }
                                  >
                                    {u.status}
                                  </Badge>
                                </td>
                                <td className="py-2 text-right font-semibold">{u.leads_count}</td>
                                <td className="py-2 text-right">{u.tasks_count}</td>
                                <td className="py-2 text-slate-600">
                                  {(u.teams || []).map((t) => t.name).join(", ") || "—"}
                                </td>
                                <td className="py-2 text-right">
                                  {!isCanonical && (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="border-amber-300 text-amber-700 hover:bg-amber-50 h-7 text-[11px]"
                                      onClick={() => openReassign(u, group.users[0])}
                                      data-testid={`reassign-btn-${u.user_id}`}
                                    >
                                      <ArrowRightLeft className="w-3 h-3 mr-1" />
                                      Reassign to canonical
                                    </Button>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* ===== AUDIT LOGS ===== */}
          <TabsContent value="audit" className="space-y-4 mt-4">
            <div className="flex flex-wrap gap-3 items-center">
              <Select value={logFilter.action} onValueChange={(v) => setLogFilter({ ...logFilter, action: v })}>
                <SelectTrigger className="w-[180px]" data-testid="log-action-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Actions</SelectItem>
                  {logMeta.actions?.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={logFilter.entity_type} onValueChange={(v) => setLogFilter({ ...logFilter, entity_type: v })}>
                <SelectTrigger className="w-[160px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Entities</SelectItem>
                  {logMeta.entity_types?.map(e => <SelectItem key={e} value={e}>{e}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button variant="outline" size="sm" onClick={loadLogs} disabled={logsLoading}>
                <RefreshCw className={`w-4 h-4 mr-1 ${logsLoading ? "animate-spin" : ""}`} /> Refresh
              </Button>
            </div>

            {logsLoading ? <div className="text-center py-8 text-slate-400"><RefreshCw className="w-6 h-6 animate-spin mx-auto" /></div> :
            logs.length === 0 ? <Card><CardContent className="p-12 text-center text-slate-400"><ScrollText className="w-10 h-10 mx-auto mb-3 opacity-40" /><p>No audit logs yet</p></CardContent></Card> : (
              <Card>
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead><tr className="border-b text-slate-500 bg-slate-50">
                        <th className="text-left py-2 px-3 font-medium">Time</th>
                        <th className="text-left py-2 px-3 font-medium">Action</th>
                        <th className="text-left py-2 px-3 font-medium">Entity</th>
                        <th className="text-left py-2 px-3 font-medium">User</th>
                        <th className="text-left py-2 px-3 font-medium">Details</th>
                      </tr></thead>
                      <tbody>
                        {logs.map(l => (
                          <tr key={l.log_id} className="border-b last:border-0 hover:bg-slate-50" data-testid={`log-row-${l.log_id}`}>
                            <td className="py-2 px-3 text-xs text-slate-500 whitespace-nowrap">{new Date(l.created_at).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</td>
                            <td className="py-2 px-3"><Badge variant="outline" className="text-xs">{l.action}</Badge></td>
                            <td className="py-2 px-3 text-xs text-slate-600">{l.entity_type}<span className="text-slate-400 ml-1">({l.entity_id?.slice(0, 12)}...)</span></td>
                            <td className="py-2 px-3 text-xs text-slate-700 font-medium">{l.user_name || l.user_id}</td>
                            <td className="py-2 px-3 text-xs text-slate-500 max-w-xs truncate">{l.details}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* ===== APPROVALS ===== */}
          <TabsContent value="approvals" className="space-y-4 mt-4">
            <div className="flex justify-between items-center">
              <div className="flex gap-2">
                <Select value={approvalFilter} onValueChange={setApprovalFilter}>
                  <SelectTrigger className="w-[140px]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setAddRuleOpen(true)} data-testid="add-approval-rule-btn">
                  <Settings className="w-4 h-4 mr-1" /> Rules
                </Button>
                <Button size="sm" onClick={() => { setApprovalForm({ type: "", entity_type: "", entity_id: "", description: "" }); setCreateApprovalOpen(true); }} data-testid="create-approval-btn">
                  <Plus className="w-4 h-4 mr-1" /> Request Approval
                </Button>
              </div>
            </div>

            {/* Approval rules */}
            {approvalRules.length > 0 && (
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm text-slate-500">Active Approval Rules</CardTitle></CardHeader>
                <CardContent className="flex flex-wrap gap-2">
                  {approvalRules.map(r => (
                    <div key={r.rule_id} className="flex items-center gap-2 bg-slate-50 rounded-lg px-3 py-1.5 border text-xs">
                      <Lock className="w-3 h-3 text-slate-400" />
                      <span className="text-slate-700">{r.name}</span>
                      <Badge variant="outline" className="text-[10px]">{r.approver_role}</Badge>
                      <button onClick={() => deleteApprovalRule(r.rule_id)} className="text-red-400 hover:text-red-600"><Trash2 className="w-3 h-3" /></button>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {/* Approval list */}
            {approvalsLoading ? <div className="text-center py-8 text-slate-400"><RefreshCw className="w-6 h-6 animate-spin mx-auto" /></div> :
            approvals.length === 0 ? <Card><CardContent className="p-12 text-center text-slate-400"><CheckCircle className="w-10 h-10 mx-auto mb-3 opacity-40" /><p>No approval requests</p></CardContent></Card> : (
              <div className="space-y-2">
                {approvals.map(a => (
                  <Card key={a.approval_id} data-testid={`approval-card-${a.approval_id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <Badge className={`text-xs ${STATUS_STYLES[a.status]}`}>{a.status}</Badge>
                            <Badge variant="outline" className="text-xs">{a.type || "general"}</Badge>
                            <span className="font-medium text-slate-900 text-sm">{a.description}</span>
                          </div>
                          <div className="flex gap-3 mt-1 text-xs text-slate-500">
                            <span>By: {a.requested_by_name}</span>
                            <span>{new Date(a.created_at).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
                            {a.reviewed_by && <span>Reviewed by: {a.reviewed_by}</span>}
                          </div>
                        </div>
                        {a.status === "pending" && (
                          <Button size="sm" variant="outline" onClick={() => { setReviewApproval(a); setReviewDecision(""); setReviewNotes(""); setReviewOpen(true); }} data-testid={`review-btn-${a.approval_id}`}>
                            <Eye className="w-4 h-4 mr-1" /> Review
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Approve / Change Role Dialog */}
        <Dialog open={approveOpen} onOpenChange={setApproveOpen}>
          <DialogContent className="sm:max-w-sm" data-testid="approve-user-dialog">
            <DialogHeader><DialogTitle>{approveUser?.status === "pending" ? "Approve User" : "Change User Role"}</DialogTitle></DialogHeader>
            {approveUser && (
              <div className="space-y-3 py-2">
                <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                  {approveUser.picture ? <img src={approveUser.picture} alt="" className="w-10 h-10 rounded-full" /> : <div className="w-10 h-10 rounded-full bg-slate-200" />}
                  <div><p className="font-semibold">{approveUser.name}</p><p className="text-xs text-slate-500">{approveUser.email}</p></div>
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block">Assign Role</label>
                  <Select value={approveRole} onValueChange={setApproveRole}>
                    <SelectTrigger data-testid="approve-role-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ceo">CEO</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                      <SelectItem value="hod_sales">HOD Sales</SelectItem>
                      <SelectItem value="sales_head">Sales Head</SelectItem>
                      <SelectItem value="sales_rep">Sales Rep</SelectItem>
                      <SelectItem value="telecaller">Telecaller</SelectItem>
                      <SelectItem value="marketing_manager">Marketing Manager</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setApproveOpen(false)}>Cancel</Button>
              <Button className="bg-emerald-600" onClick={handleApprove} data-testid="confirm-approve-btn">{approveUser?.status === "pending" ? "Approve" : "Update Role"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Reject User Dialog */}
        <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
          <DialogContent className="sm:max-w-sm" data-testid="reject-user-dialog">
            <DialogHeader><DialogTitle>Reject User — {rejectUser?.name}</DialogTitle></DialogHeader>
            <div className="py-2">
              <label className="text-sm font-medium mb-1 block">Reason (optional)</label>
              <Textarea value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} rows={3} data-testid="reject-reason" />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setRejectOpen(false)}>Cancel</Button>
              <Button variant="destructive" onClick={handleReject} data-testid="confirm-reject-btn">Reject</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Add User Dialog */}
        <Dialog open={addUserOpen} onOpenChange={setAddUserOpen}>
          <DialogContent className="sm:max-w-sm" data-testid="add-user-dialog">
            <DialogHeader><DialogTitle>Add New User</DialogTitle></DialogHeader>
            <div className="space-y-3 py-2">
              <div>
                <label className="text-sm font-medium mb-1 block">Name</label>
                <Input
                  value={addUserForm.name}
                  onChange={(e) => setAddUserForm({ ...addUserForm, name: e.target.value })}
                  placeholder="e.g. Rahul Sharma"
                  data-testid="add-user-name"
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Gmail Email</label>
                <Input
                  type="email"
                  value={addUserForm.email}
                  onChange={(e) => setAddUserForm({ ...addUserForm, email: e.target.value })}
                  placeholder="e.g. rahul@gmail.com"
                  data-testid="add-user-email"
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Assign Role</label>
                <Select value={addUserForm.role} onValueChange={(v) => setAddUserForm({ ...addUserForm, role: v })}>
                  <SelectTrigger data-testid="add-user-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ceo">CEO</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="hod_sales">HOD Sales</SelectItem>
                    <SelectItem value="sales_head">Sales Head</SelectItem>
                    <SelectItem value="sales_rep">Sales Rep</SelectItem>
                    <SelectItem value="telecaller">Telecaller</SelectItem>
                    <SelectItem value="marketing_manager">Marketing Manager</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <p className="text-xs text-slate-500">User will appear as pending. They need to sign in via Google with this email to activate their account.</p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setAddUserOpen(false)}>Cancel</Button>
              <Button className="bg-slate-900 hover:bg-slate-800" onClick={handleAddUser} data-testid="confirm-add-user-btn">Add User</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Create Approval Dialog */}
        <Dialog open={createApprovalOpen} onOpenChange={setCreateApprovalOpen}>
          <DialogContent className="sm:max-w-md" data-testid="create-approval-dialog">
            <DialogHeader><DialogTitle>Request Approval</DialogTitle></DialogHeader>
            <div className="space-y-3 py-2">
              <div>
                <label className="text-sm font-medium mb-1 block">Type</label>
                <Select value={approvalForm.type} onValueChange={(v) => setApprovalForm({ ...approvalForm, type: v })}>
                  <SelectTrigger data-testid="approval-type"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="discount">Discount Approval</SelectItem>
                    <SelectItem value="unit_release">Unit Release</SelectItem>
                    <SelectItem value="pricing_change">Pricing Change</SelectItem>
                    <SelectItem value="role_change">Role Change</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div><label className="text-sm font-medium mb-1 block">Entity Type</label><Input value={approvalForm.entity_type} onChange={(e) => setApprovalForm({ ...approvalForm, entity_type: e.target.value })} placeholder="e.g. negotiation" /></div>
                <div><label className="text-sm font-medium mb-1 block">Entity ID</label><Input value={approvalForm.entity_id} onChange={(e) => setApprovalForm({ ...approvalForm, entity_id: e.target.value })} placeholder="e.g. neg_abc123" /></div>
              </div>
              <div><label className="text-sm font-medium mb-1 block">Description</label><Textarea value={approvalForm.description} onChange={(e) => setApprovalForm({ ...approvalForm, description: e.target.value })} rows={3} data-testid="approval-description" /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCreateApprovalOpen(false)}>Cancel</Button>
              <Button onClick={submitApproval} data-testid="submit-approval-btn">Submit</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Review Approval Dialog */}
        <Dialog open={reviewOpen} onOpenChange={setReviewOpen}>
          <DialogContent className="sm:max-w-sm" data-testid="review-dialog">
            <DialogHeader><DialogTitle>Review Approval</DialogTitle></DialogHeader>
            {reviewApproval && (
              <div className="space-y-3 py-2">
                <div className="bg-slate-50 rounded-lg p-3">
                  <p className="text-sm font-medium">{reviewApproval.description}</p>
                  <p className="text-xs text-slate-500 mt-1">Type: {reviewApproval.type} | By: {reviewApproval.requested_by_name}</p>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <button className={`p-3 rounded-lg border text-center ${reviewDecision === "approved" ? "ring-2 ring-emerald-400 bg-emerald-50" : "hover:bg-slate-50"}`}
                    onClick={() => setReviewDecision("approved")} data-testid="review-approve">
                    <CheckCircle className="w-5 h-5 mx-auto mb-1 text-emerald-600" /><span className="text-xs">Approve</span>
                  </button>
                  <button className={`p-3 rounded-lg border text-center ${reviewDecision === "rejected" ? "ring-2 ring-red-400 bg-red-50" : "hover:bg-slate-50"}`}
                    onClick={() => setReviewDecision("rejected")} data-testid="review-reject">
                    <XCircle className="w-5 h-5 mx-auto mb-1 text-red-600" /><span className="text-xs">Reject</span>
                  </button>
                </div>
                <div><label className="text-sm font-medium mb-1 block">Notes</label><Textarea value={reviewNotes} onChange={(e) => setReviewNotes(e.target.value)} rows={2} /></div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setReviewOpen(false)}>Cancel</Button>
              <Button onClick={submitReview} disabled={!reviewDecision} data-testid="submit-review-btn">Submit</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Add Approval Rule Dialog */}
        <Dialog open={addRuleOpen} onOpenChange={setAddRuleOpen}>
          <DialogContent className="sm:max-w-md" data-testid="add-rule-dialog">
            <DialogHeader><DialogTitle>Approval Rules</DialogTitle></DialogHeader>
            {/* Existing rules */}
            {approvalRules.length > 0 && (
              <div className="space-y-1 mb-3">
                {approvalRules.map(r => (
                  <div key={r.rule_id} className="flex items-center justify-between text-sm p-2 bg-slate-50 rounded">
                    <div><p className="font-medium text-slate-800">{r.name}</p><p className="text-xs text-slate-500">{r.description}</p></div>
                    <Button variant="ghost" size="sm" className="text-red-500" onClick={() => deleteApprovalRule(r.rule_id)}><Trash2 className="w-3 h-3" /></Button>
                  </div>
                ))}
              </div>
            )}
            <div className="space-y-3 border-t pt-3">
              <p className="text-sm font-medium">Add New Rule</p>
              <div><Input value={ruleForm.name} onChange={(e) => setRuleForm({ ...ruleForm, name: e.target.value })} placeholder="Rule name" data-testid="rule-name" /></div>
              <div><Input value={ruleForm.description} onChange={(e) => setRuleForm({ ...ruleForm, description: e.target.value })} placeholder="Description" /></div>
              <div className="grid grid-cols-2 gap-2">
                <Select value={ruleForm.trigger_type} onValueChange={(v) => setRuleForm({ ...ruleForm, trigger_type: v })}>
                  <SelectTrigger><SelectValue placeholder="Trigger type" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="discount_threshold">Discount Threshold</SelectItem>
                    <SelectItem value="unit_release">Unit Release</SelectItem>
                    <SelectItem value="pricing_change">Pricing Change</SelectItem>
                    <SelectItem value="role_change">Role Change</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={ruleForm.approver_role} onValueChange={(v) => setRuleForm({ ...ruleForm, approver_role: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="sales_head">Sales Head</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setAddRuleOpen(false)}>Close</Button>
              <Button onClick={createApprovalRule} disabled={!ruleForm.name} data-testid="save-rule-btn">Add Rule</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Deactivate User with Reassignment Dialog */}
        <Dialog open={deactivateOpen} onOpenChange={setDeactivateOpen}>
          <DialogContent className="sm:max-w-lg" data-testid="deactivate-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600">
                <AlertTriangle className="w-5 h-5" /> Deactivate User
              </DialogTitle>
            </DialogHeader>
            {deactivateUser && (
              <div className="space-y-4">
                <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                  {deactivateUser.picture ? (
                    <img src={deactivateUser.picture} alt="" className="w-10 h-10 rounded-full" />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center">
                      <UserX className="w-5 h-5 text-slate-500" />
                    </div>
                  )}
                  <div>
                    <p className="font-medium text-slate-900">{deactivateUser.name}</p>
                    <p className="text-sm text-slate-500">{deactivateUser.email}</p>
                  </div>
                </div>

                {/* Workload Summary */}
                {deactivateWorkload && deactivateWorkload.total > 0 && (
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-sm font-medium text-amber-800 mb-2">This user has assigned work:</p>
                    <div className="grid grid-cols-3 gap-2 text-center">
                      <div className="bg-white rounded p-2">
                        <p className="text-lg font-bold text-slate-900">{deactivateWorkload.leads}</p>
                        <p className="text-xs text-slate-500">Leads</p>
                      </div>
                      <div className="bg-white rounded p-2">
                        <p className="text-lg font-bold text-slate-900">{deactivateWorkload.pending_tasks}</p>
                        <p className="text-xs text-slate-500">Tasks</p>
                      </div>
                      <div className="bg-white rounded p-2">
                        <p className="text-lg font-bold text-slate-900">{deactivateWorkload.pending_visits}</p>
                        <p className="text-xs text-slate-500">Visits</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Reassignment Option */}
                {deactivateWorkload && deactivateWorkload.total > 0 && (
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">Reassign work to (recommended):</label>
                    <Select value={reassignTo} onValueChange={setReassignTo}>
                      <SelectTrigger data-testid="reassign-to-select">
                        <SelectValue placeholder="Select user to reassign work..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="">Don't reassign (leave orphaned)</SelectItem>
                        {users.filter(u => u.user_id !== deactivateUser.user_id && (u.status === "active" || !u.status)).map(u => (
                          <SelectItem key={u.user_id} value={u.user_id}>{u.name} ({ROLE_LABELS[u.role] || u.role})</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {!reassignTo && (
                      <p className="text-xs text-amber-600">Warning: Without reassignment, leads/tasks will remain assigned to this inactive user.</p>
                    )}
                  </div>
                )}

                <p className="text-sm text-slate-600">
                  This will prevent {deactivateUser.name} from logging in. You can reactivate them later if needed.
                </p>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeactivateOpen(false)}>Cancel</Button>
              <Button variant="destructive" onClick={handleDeactivate} disabled={deactivating} data-testid="confirm-deactivate-btn">
                {deactivating ? <RefreshCw className="w-4 h-4 animate-spin mr-1" /> : <UserX className="w-4 h-4 mr-1" />}
                Deactivate{reassignTo ? " & Reassign" : ""}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Bulk Reassign Dialog */}
        <Dialog open={bulkReassignOpen} onOpenChange={setBulkReassignOpen}>
          <DialogContent className="sm:max-w-lg" data-testid="bulk-reassign-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <ArrowRightLeft className="w-5 h-5 text-blue-600" /> Bulk Reassign Work
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-slate-600">
                Transfer all leads, tasks, and visits from one user to another.
              </p>

              {/* From User */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">From user:</label>
                <Select value={bulkFromUser} onValueChange={loadBulkFromWorkload}>
                  <SelectTrigger data-testid="bulk-from-select">
                    <SelectValue placeholder="Select source user..." />
                  </SelectTrigger>
                  <SelectContent>
                    {users.map(u => (
                      <SelectItem key={u.user_id} value={u.user_id}>
                        {u.name} ({ROLE_LABELS[u.role] || u.role}) {u.status === "inactive" && "- Inactive"}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* From User Workload */}
              {bulkFromWorkload && (
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-sm font-medium text-slate-700 mb-2">Work to reassign:</p>
                  <div className="grid grid-cols-5 gap-2 text-center text-xs">
                    <div><p className="font-bold text-lg text-slate-900">{bulkFromWorkload.leads}</p><p className="text-slate-500">Leads</p></div>
                    <div><p className="font-bold text-lg text-slate-900">{bulkFromWorkload.pending_tasks}</p><p className="text-slate-500">Tasks</p></div>
                    <div><p className="font-bold text-lg text-slate-900">{bulkFromWorkload.pending_visits}</p><p className="text-slate-500">Visits</p></div>
                    <div><p className="font-bold text-lg text-slate-900">{bulkFromWorkload.active_deal_rooms}</p><p className="text-slate-500">Deals</p></div>
                    <div><p className="font-bold text-lg text-slate-900">{bulkFromWorkload.active_eois}</p><p className="text-slate-500">EOIs</p></div>
                  </div>
                </div>
              )}

              {/* To User */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">To user:</label>
                <Select value={bulkToUser} onValueChange={setBulkToUser}>
                  <SelectTrigger data-testid="bulk-to-select">
                    <SelectValue placeholder="Select target user..." />
                  </SelectTrigger>
                  <SelectContent>
                    {users.filter(u => u.user_id !== bulkFromUser && (u.status === "active" || !u.status)).map(u => (
                      <SelectItem key={u.user_id} value={u.user_id}>{u.name} ({ROLE_LABELS[u.role] || u.role})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setBulkReassignOpen(false)}>Cancel</Button>
              <Button onClick={handleBulkReassign} disabled={!bulkFromUser || !bulkToUser || bulkReassigning} data-testid="confirm-bulk-reassign-btn">
                {bulkReassigning ? <RefreshCw className="w-4 h-4 animate-spin mr-1" /> : <ArrowRightLeft className="w-4 h-4 mr-1" />}
                Reassign All
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Rename User Dialog */}
        <Dialog open={renameOpen} onOpenChange={setRenameOpen}>
          <DialogContent className="sm:max-w-md" data-testid="rename-user-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Pencil className="w-4 h-4 text-indigo-600" />
                Rename user
              </DialogTitle>
            </DialogHeader>
            <div className="py-3 space-y-3">
              <div className="text-sm text-slate-600">
                <div><span className="text-slate-400">Email:</span> {renameUser?.email}</div>
                <div><span className="text-slate-400">Current name:</span> {renameUser?.name || <span className="italic text-slate-400">empty</span>}</div>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-700 block mb-1">Display name</label>
                <Input
                  autoFocus
                  value={renameValue}
                  onChange={(e) => setRenameValue(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") handleRenameSubmit(); }}
                  placeholder="e.g. Raj Mishra"
                  data-testid="rename-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setRenameOpen(false)} data-testid="rename-cancel">Cancel</Button>
              <Button
                className="bg-indigo-600 text-white hover:bg-indigo-700"
                onClick={handleRenameSubmit}
                disabled={!renameValue.trim()}
                data-testid="rename-submit"
              >
                Save
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Reassign Confirmation Dialog */}
        <Dialog open={!!dupReassignFrom} onOpenChange={(v) => !v && setDupReassignFrom(null)}>
          <DialogContent className="sm:max-w-md" data-testid="reassign-dialog">
            <DialogHeader>
              <DialogTitle>Reassign user</DialogTitle>
            </DialogHeader>
            {dupReassignFrom && dupReassignTo && (
              <div className="space-y-3 text-sm">
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <p className="text-amber-900">
                    Move everything from{" "}
                    <strong>
                      {dupReassignFrom.name || dupReassignFrom.email}
                    </strong>{" "}
                    <span className="font-mono text-[10px] text-amber-700">({dupReassignFrom.user_id})</span>
                    <br />
                    to{" "}
                    <strong>
                      {dupReassignTo.name || dupReassignTo.email}
                    </strong>{" "}
                    <span className="font-mono text-[10px] text-amber-700">({dupReassignTo.user_id})</span>.
                  </p>
                  <p className="text-xs text-amber-700 mt-2">
                    This will move <strong>{dupReassignFrom.leads_count}</strong> leads and{" "}
                    <strong>{dupReassignFrom.tasks_count}</strong> open tasks, plus replace the user in any team
                    membership. Cannot be undone.
                  </p>
                </div>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={dupReassignDeactivate}
                    onChange={(e) => setDupReassignDeactivate(e.target.checked)}
                    data-testid="reassign-deactivate-checkbox"
                  />
                  <span>Deactivate the source user after reassignment</span>
                </label>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setDupReassignFrom(null)} disabled={dupReassignBusy}>
                Cancel
              </Button>
              <Button
                className="bg-amber-600 text-white hover:bg-amber-700"
                onClick={performReassign}
                disabled={dupReassignBusy}
                data-testid="reassign-confirm"
              >
                {dupReassignBusy ? "Reassigning…" : "Reassign"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default GovernancePage;
